import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isSolid = scrolled || !isHome;

  const navLinkClass = (active: boolean) =>
    `hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
      isSolid
        ? active
          ? 'text-primary-700 bg-primary-50'
          : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
        : active
          ? 'text-white bg-white/20'
          : 'text-white/80 hover:text-white hover:bg-white/10'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isSolid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-4">
        <Link
          to="/"
          className={`font-heading font-bold text-xl tracking-tight whitespace-nowrap transition-colors duration-300 ${
            isSolid ? 'text-foreground-900' : 'text-white'
          }`}
        >
          OOPBuy Spreadsheet
        </Link>

        <div className="flex items-center gap-2 md:gap-4">
          <Link to="/" className={navLinkClass(isHome)}>
            <i className="ri-home-4-line"></i>
            <span>Home</span>
          </Link>

          <Link
            to="/artikel/oopbuy-spreadsheet-guide"
            className={navLinkClass(location.pathname.startsWith('/artikel'))}
          >
            <i className="ri-book-open-line"></i>
            <span>Guide</span>
          </Link>

          <div className="relative hidden md:block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isSolid
                  ? 'border-foreground-300 text-foreground-700 hover:border-foreground-500 bg-background-50'
                  : 'border-white/40 text-white hover:border-white/70 bg-white/10 backdrop-blur-sm'
              }`}
            >
              <span>Kategorien</span>
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-xl overflow-hidden max-h-[28rem] overflow-y-auto">
                <div className="px-4 py-3 bg-secondary-100 border-b border-background-200/60">
                  <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                    Alle Kategorien
                  </p>
                </div>
                {categories.map((cat, idx) => (
                  <a
                    key={idx}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-secondary-100 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={cat.icon}></i>
                    </span>
                    <span>{cat.name}</span>
                    <i className="ri-arrow-right-up-line ml-auto text-foreground-300 text-xs"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <span>OOPBuy Spreadsheet</span>
            <i className="ri-arrow-right-up-line"></i>
          </a>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground-900 text-white text-sm font-semibold hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <span>Buy It Now</span>
            <i className="ri-arrow-right-up-line"></i>
          </a>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${
              isSolid
                ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                : 'bg-white/15 text-white hover:bg-white/25'
            }`}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-home-4-line"></i>
              </span>
              <span>Home</span>
            </Link>

            <Link
              to="/artikel/oopbuy-spreadsheet-guide"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                <i className="ri-book-open-line"></i>
              </span>
              <span>OOPBuy Guide</span>
            </Link>

            <div className="px-4 py-2">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">
                Kategorien
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat, idx) => (
                  <a
                    key={idx}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-foreground-600 hover:bg-secondary-100 transition-colors cursor-pointer"
                  >
                    <i className={cat.icon}></i>
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 mx-4 mt-2 px-4 py-3 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-shopping-bag-line"></i>
              <span>OOPBuy Spreadsheet entdecken</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}